import { AutocompleteFilter } from '@shigo-ui/ui';
import { useAgencyCampaignContext } from '../../../agencyCampaignContext';
import { AgencyCampaignSection, AgencyEditorMode } from '../../../../agency/model';
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  DashboardTableStatusTone,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import { Button, Tooltip, TooltipContent, TooltipTrigger, toast } from '@shigo-ui/ui';
import { Plus } from 'lucide-react';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useLineItemMutations, useLineItemsQuery } from '../../hooks';
import {
  campaignPageSizes,
  formatLineItemCurrency,
  formatLineItemDate,
  LineItemDeliveryStatus,
  resolveLineItemDeliveryStatusLabel,
  type LineItem,
} from '../../model';
import { useLineItemsRouteFilters } from '../../useLineItemsRouteFilters';
import { LineItemActions } from './LineItemActions';
import { LineItemCompletion } from './LineItemCompletion';
import { LineItemFilters } from './LineItemFilters';

export function LineItemsTable({ campaignId }: { campaignId: string }) {
  const scope = useAgencyCampaignContext();
  const { can } = useShigoPermissions();
  const canWrite = Boolean(scope) || can(ShigoPermission.MadBuyCampaignWrite);
  const [duplicatingIds, setDuplicatingIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const {
    setAgencies,
    filters,
    queryFilter,
    setDeliveryStatuses,
    setPagination,
    setSearch,
    setStatuses,
  } = useLineItemsRouteFilters(campaignId);
  const query = useLineItemsQuery(queryFilter);
  const mutations = useLineItemMutations();
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<LineItem>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('lineItems.name'),
          headerClassName: 'w-[30%] pl-4',
          className: 'pl-4 pr-2',
          cell: (lineItem) => {
            const name = canWrite ? (
              <Link
                to={
                  scope
                    ? '/a/$agencyId/campaigns/$section'
                    : '/campaigns/campaigns/overview/$campaignId/line-items/edit/$lineItemId'
                }
                params={{
                  campaignId,
                  lineItemId: lineItem.id,
                  agencyId: scope?.agency.id ?? '',
                  section: AgencyCampaignSection.LineItems,
                }}
                search={
                  scope
                    ? { editor: AgencyEditorMode.Edit, id: lineItem.id, campaign: campaignId }
                    : {}
                }
                className={dashboardTableTextActionClassName()}
              >
                {lineItem.name}
              </Link>
            ) : (
              <span>{lineItem.name}</span>
            );
            return (
              <Tooltip>
                <TooltipTrigger asChild>{name}</TooltipTrigger>
                <TooltipContent>{lineItem.name}</TooltipContent>
              </Tooltip>
            );
          },
        },
        {
          id: 'deliveryStatus',
          kind: DashboardDataTableColumnKind.Status,
          header: dashboardT('lineItems.deliveryStatus'),
          label: (lineItem) => resolveLineItemDeliveryStatusLabel(lineItem.deliveryStatus),
          tone: (lineItem) => deliveryStatusTone(lineItem.deliveryStatus),
        },
        {
          id: 'startDate',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('lineItems.startDate'),
          value: (lineItem) => formatLineItemDate(lineItem.startDate),
        },
        {
          id: 'endDate',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('lineItems.endDate'),
          value: (lineItem) => formatLineItemDate(lineItem.endDate),
        },
        {
          id: 'cpm',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('lineItems.cpm'),
          value: (lineItem) => formatLineItemCurrency(lineItem.cpm),
        },
        {
          id: 'completion',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('lineItems.completion'),
          headerClassName: 'w-52 pr-4',
          className: 'pr-4',
          cell: (lineItem) => <LineItemCompletion lineItem={lineItem} />,
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          hidden: !canWrite,
          cell: (lineItem) => (
            <LineItemActions
              campaignId={campaignId}
              lineItemId={lineItem.id}
              name={lineItem.name}
              pending={duplicatingIds.has(lineItem.id)}
              onDuplicate={(deepCopyCreatives) => {
                setDuplicatingIds((current) => new Set(current).add(lineItem.id));
                mutations.duplicate.mutate(
                  { id: lineItem.id, deepCopyCreatives },
                  {
                    onSuccess: () => toast.success(dashboardT('lineItems.duplicated')),
                    onSettled: () => {
                      setDuplicatingIds((current) => {
                        const next = new Set(current);
                        next.delete(lineItem.id);
                        return next;
                      });
                    },
                  },
                );
              }}
            />
          ),
        },
      ]),
    [campaignId, canWrite, duplicatingIds, mutations.duplicate, scope],
  );

  return (
    <DashboardDataTable
      title={dashboardT('lineItems.title')}
      tableLabel={dashboardT('lineItems.title')}
      columns={columns}
      items={query.data?.content ?? []}
      getRowKey={(lineItem) => lineItem.id}
      loading={query.isLoading}
      loadingMessage={dashboardT('lineItems.loading')}
      emptyMessage={dashboardT('lineItems.empty')}
      errorMessage={query.isError ? dashboardT('lineItems.loadError') : undefined}
      minWidthClassName="min-w-[1080px]"
      toolbar={
        <>
          {!scope ? (
            <AutocompleteFilter
              label={dashboardT('agencyAssignment.filter')}
              options={[]}
              selectedIds={filters.agencyIds ?? []}
              onSelectedIdsChange={setAgencies}
              selectAll={false}
              emptyMessage={dashboardT('agencyWorkspace.unavailable')}
              className="hidden"
            />
          ) : null}
          <LineItemsSearchInput
            key={filters.name}
            initialValue={filters.name}
            onChange={setSearch}
          />
          <LineItemFilters
            selectedStatuses={filters.status}
            selectedDeliveryStatuses={filters.deliveryStatus}
            onStatusesChange={setStatuses}
            onDeliveryStatusesChange={setDeliveryStatuses}
          />
          {canWrite ? (
            <Button asChild>
              <Link
                to={
                  scope
                    ? '/a/$agencyId/campaigns/$section'
                    : '/campaigns/campaigns/overview/$campaignId/line-items/create'
                }
                params={{
                  campaignId,
                  agencyId: scope?.agency.id ?? '',
                  section: AgencyCampaignSection.LineItems,
                }}
                search={scope ? { editor: AgencyEditorMode.Create, campaign: campaignId } : {}}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {dashboardT('madBuy.lineItems.newTitle')}
              </Link>
            </Button>
          ) : null}
        </>
      }
      pagination={{
        value: {
          page: filters.page,
          pageSize: filters.size,
          totalItems: query.data?.totalElements ?? 0,
        },
        pageSizes: campaignPageSizes,
        disabled: query.isFetching,
        onChange: (next) => {
          setPagination(next.page, next.pageSize);
        },
      }}
    />
  );
}

function LineItemsSearchInput({
  initialValue,
  onChange,
}: {
  initialValue: string;
  onChange: (value: string) => void;
}) {
  // Emits only typed changes, so mounting (or leaving while a route loads) never navigates.
  const search = useDebouncedValue(initialValue, onChange, 650);
  return (
    <DashboardTableSearchInput
      value={search.draft}
      aria-label={dashboardT('lineItems.search')}
      placeholder={dashboardT('lineItems.search')}
      onChange={(event) => {
        search.setDraft(event.target.value);
      }}
    />
  );
}

function deliveryStatusTone(status: string | undefined) {
  if (status === LineItemDeliveryStatus.Delivering) return DashboardTableStatusTone.Info;
  if (
    status === LineItemDeliveryStatus.GoalMet ||
    status === LineItemDeliveryStatus.Completed ||
    status === LineItemDeliveryStatus.Ready
  ) {
    return DashboardTableStatusTone.Success;
  }
  return DashboardTableStatusTone.Neutral;
}
